/**
 * GalaxyMiniThumbnail — W49
 *
 * Lightweight inline SVG starfield that EVOKES the Galaxy surface.
 * ~40 dots on a dark navy gradient, deterministic seeded pseudo-random
 * so positions never flicker on re-render.
 *
 * NOT a Three.js mount. Static SVG only — safe to render on every screen.
 *
 * Props:
 *   size — pixel dimension of the square (both width and height).
 *           "small" preset ≈ 36px, "large" preset ≈ 64px.
 *           Accepts an explicit number for full control.
 */

import React, { useMemo } from "react";

export type GalaxyMiniThumbnailSize = "small" | "large" | number;

interface GalaxyMiniThumbnailProps {
  size?: GalaxyMiniThumbnailSize;
  className?: string;
  style?: React.CSSProperties;
}

/** Deterministic seeded LCG pseudo-random — no flicker on re-render. */
function seededRandom(seed: number): () => number {
  let s = seed;
  return function next() {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    return (s >>> 0) / 0xffffffff;
  };
}

const SEED = 0xc0ffee49; // W49 seed — fixed forever
const DOT_COUNT = 40;

interface Dot {
  cx: number;
  cy: number;
  r: number;
  opacity: number;
}

/** Generate dot positions once in [0,100] viewBox space. */
function generateDots(): Dot[] {
  const rng = seededRandom(SEED);
  return Array.from({ length: DOT_COUNT }, () => ({
    cx: rng() * 100,
    cy: rng() * 100,
    // radius: 0.5 – 1.5 (relative to 100x100 viewBox)
    r: 0.5 + rng() * 1.0,
    // opacity: 0.35 – 1.0
    opacity: 0.35 + rng() * 0.65,
  }));
}

const DOTS: Dot[] = generateDots();

function resolveSize(size: GalaxyMiniThumbnailSize): number {
  if (size === "small") return 36;
  if (size === "large") return 64;
  return size;
}

export default function GalaxyMiniThumbnail({
  size = "small",
  className,
  style,
}: GalaxyMiniThumbnailProps) {
  const px = resolveSize(size);
  const gradId = "gmtBg";
  const glowId = "gmtGlow";

  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
      style={{
        display: "block",
        flexShrink: 0,
        borderRadius: 6,
        ...style,
      }}
    >
      <defs>
        {/* Dark navy radial gradient — #050510 center → #0a0a1a edge */}
        <radialGradient id={gradId} cx="50%" cy="50%" r="70%" fx="40%" fy="35%">
          <stop offset="0%" stopColor="#0a0a1a" />
          <stop offset="60%" stopColor="#050510" />
          <stop offset="100%" stopColor="#020208" />
        </radialGradient>

        {/* Subtle centre glow — evokes galaxy core */}
        <radialGradient id={glowId} cx="42%" cy="38%" r="35%">
          <stop offset="0%" stopColor="#2a1f6e" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#050510" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Background */}
      <rect width="100" height="100" fill={`url(#${gradId})`} />

      {/* Galaxy core glow */}
      <rect width="100" height="100" fill={`url(#${glowId})`} />

      {/* Stars */}
      {DOTS.map((d, i) => (
        <circle
          key={i}
          cx={d.cx}
          cy={d.cy}
          r={d.r}
          fill="#FFFFFF"
          opacity={d.opacity}
        />
      ))}
    </svg>
  );
}
